/**
 * Product Grid AJAX Loading
 * Loads product details directly in the collection grid without page navigation
 *
 * 3DMARK adjustment:
 * - Preserve Shopify locale path prefix when loading products by AJAX.
 * - Build AJAX product breadcrumb from sidebar active menu hierarchy.
 */

class ProductGridAjax {
  constructor() {
    this.grid = document.querySelector('.main-products-grid__results');
    this.productGrid = null;
    this.originalContent = null;
    this.currentProduct = null;
    this.currentProductTitle = '';

    this.init();
    this.checkForProductView();
  }

  init() {
    if (!this.grid) return;

    this.productGrid = this.grid.querySelector('ul.grid');

    this.storeOriginalContent();
    this.setupEventListeners();
    this.setupHistoryHandling();
  }

  storeOriginalContent() {
    this.originalContent = this.grid.innerHTML;
  }

  setupEventListeners() {
    document.addEventListener('click', (event) => {
      const productLink = event.target.closest('.js-prod-link');

      if (productLink && this.isWithinGrid(productLink)) {
        event.preventDefault();
        event.stopPropagation();

        const productUrl = productLink.href;
        this.loadProduct(productUrl);
      }

      if (event.target.closest('.js-back-to-grid')) {
        event.preventDefault();
        this.restoreGrid();
      }
    });
  }

  isWithinGrid(element) {
    return element.closest('.main-products-grid__results') !== null;
  }

  getLocalePrefix() {
    const path = window.location.pathname;

    if (path === '/en' || path.startsWith('/en/')) {
      return '/en';
    }

    if (path === '/ja' || path.startsWith('/ja/')) {
      return '/ja';
    }

    return '';
  }

  getLocaleCode() {
    const prefix = this.getLocalePrefix();

    if (prefix === '/en') return 'en';
    if (prefix === '/ja') return 'ja';

    const htmlLang = document.documentElement.lang || '';

    if (htmlLang.toLowerCase().startsWith('en')) return 'en';
    if (htmlLang.toLowerCase().startsWith('ja')) return 'ja';

    return 'zh';
  }

  localizePath(path) {
    const localePrefix = this.getLocalePrefix();

    if (!path) return path;

    if (/^https?:\/\//.test(path)) {
      const url = new URL(path);
      path = url.pathname + url.search + url.hash;
    }

    if (!localePrefix) {
      return path.replace(/^\/(en|ja)(?=\/)/, '');
    }

    if (path === localePrefix || path.startsWith(`${localePrefix}/`)) {
      return path;
    }

    const cleanPath = path.replace(/^\/(en|ja)(?=\/)/, '');

    return `${localePrefix}${cleanPath}`;
  }

  buildProductUrlFromHandle(handle) {
    const localePrefix = this.getLocalePrefix();

    return `${localePrefix}/products/${handle}`;
  }

  extractHandleFromUrl(url) {
    try {
      const parsedUrl = new URL(url, window.location.origin);
      const match = parsedUrl.pathname.match(/\/products\/([^/]+)/);

      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      const match = url.match(/\/products\/([^?#/]+)/);

      return match ? decodeURIComponent(match[1]) : '';
    }
  }

  async loadProduct(productUrl, updateHistory = true, variantId = null) {
    this.showLoadingState();

    try {
      const handle = this.extractHandleFromUrl(productUrl);

      if (!handle) {
        throw new Error('Product handle not found');
      }

      const localizedProductUrl = this.localizePath(`/products/${handle}`);

      const [htmlResponse, jsonResponse] = await Promise.all([
        fetch(localizedProductUrl, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        }),
        fetch(`${localizedProductUrl}.json`, {
          headers: {
            Accept: 'application/json'
          }
        }).catch(() => null)
      ]);

      if (!htmlResponse.ok) {
        throw new Error('Failed to load product');
      }

      const html = await htmlResponse.text();

      if (jsonResponse && jsonResponse.ok) {
        const data = await jsonResponse.json();
        this.currentProduct = data.product;
      }

      if (updateHistory) {
        const currentPath = window.location.pathname;
        const currentParams = new URLSearchParams(window.location.search);

        currentParams.set('view', handle);

        if (variantId) {
          currentParams.set('variant', variantId);
        } else {
          currentParams.delete('variant');
        }

        const newUrl = `${currentPath}?${currentParams.toString()}`;

        window.history.pushState(
          {
            type: 'product',
            handle,
            variant: variantId,
            previousSearch: window.location.search
          },
          '',
          newUrl
        );
      }

      this.renderProductPage(html, variantId);
    } catch (error) {
      console.error('Error loading product:', error);
      this.showErrorState();
    }
  }

  showLoadingState() {
    const localeCode = this.getLocaleCode();

    let loadingText = 'Loading product details...';

    if (localeCode === 'zh') {
      loadingText = '商品載入中...';
    } else if (localeCode === 'ja') {
      loadingText = '商品を読み込んでいます...';
    }

    this.grid.innerHTML = `
      <div class="product-loading-state text-center py-12">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">${loadingText}</span>
        </div>
        <p class="mt-4 text-gray-600">${loadingText}</p>
      </div>
    `;
  }

  showErrorState() {
    const localeCode = this.getLocaleCode();

    let errorText = 'Failed to load product. Please try again.';
    let backText = 'Back to Products';

    if (localeCode === 'zh') {
      errorText = '商品載入失敗，請再試一次。';
      backText = '返回商品列表';
    } else if (localeCode === 'ja') {
      errorText = '商品の読み込みに失敗しました。もう一度お試しください。';
      backText = '商品一覧に戻る';
    }

    this.grid.innerHTML = `
      <div class="product-error-state text-center py-12">
        <p class="text-red-600">${errorText}</p>
        <button class="btn btn-primary mt-4 js-back-to-grid">${backText}</button>
      </div>
    `;
  }

  renderProductPage(html, variantId = null) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    this.currentProductTitle = this.extractProductTitleFromDocument(doc);

    const inlineStyles = doc.querySelectorAll('style');

    let fullProductContent = '';
    let stickyAtcPanelHtml = '';

    const product = doc.querySelector('.product.js-product');

    if (product) {
      const container = product.closest('.container');

      if (container) {
        fullProductContent += container.outerHTML;
      } else {
        fullProductContent += `<div class="container">${product.outerHTML}</div>`;
      }
    }

    const productDetailsContainer = doc.querySelector('.container.reading-width');

    if (!productDetailsContainer) {
      const productDetails =
        doc.querySelector('.product-details__block') ||
        doc.querySelector('tabbed-content');

      if (productDetails) {
        const detailsContainer = productDetails.closest('.container');

        if (detailsContainer) {
          fullProductContent += detailsContainer.outerHTML;
        }
      }
    } else {
      fullProductContent += productDetailsContainer.outerHTML;
    }

    const stickyAtcPanel = doc.querySelector('sticky-atc-panel');

    if (stickyAtcPanel) {
      stickyAtcPanelHtml = stickyAtcPanel.outerHTML;
    }

    if (!fullProductContent) {
      const shopifySections = doc.querySelectorAll('.shopify-section');

      shopifySections.forEach((section) => {
        if (
          section.querySelector('.product') ||
          section.querySelector('.product-details') ||
          section.querySelector('tabbed-content')
        ) {
          fullProductContent += section.innerHTML;
        }
      });
    }

    if (!fullProductContent) {
      console.error('Could not find any product content');
      this.showErrorState();
      return;
    }

    let stylesHtml = '';

    inlineStyles.forEach((style) => {
      stylesHtml += style.outerHTML;
    });

    this.renderExtractedContent(fullProductContent, stylesHtml, variantId, stickyAtcPanelHtml);
  }

  extractProductTitleFromDocument(doc) {
    const titleSelectors = [
      '.product-title',
      '.product__title',
      'h1.product-title',
      'h1'
    ];

    for (const selector of titleSelectors) {
      const titleEl = doc.querySelector(selector);

      if (titleEl && titleEl.textContent.trim()) {
        return titleEl.textContent.trim();
      }
    }

    if (this.currentProduct && this.currentProduct.title) {
      return this.currentProduct.title;
    }

    return this.getLocaleCode() === 'ja'
      ? '商品'
      : this.getLocaleCode() === 'zh'
        ? '商品'
        : 'Product';
  }

  getLocaleLabels() {
    const localeCode = this.getLocaleCode();

    if (localeCode === 'en') {
      return {
        home: 'HOME',
        collections: 'Online shopping',
        products: 'Products',
        backTo: 'Back to'
      };
    }

    if (localeCode === 'ja') {
      return {
        home: 'ホーム',
        collections: '製品一覧',
        products: '商品',
        backTo: '戻る：'
      };
    }

    return {
      home: '首頁',
      collections: '商品列表',
      products: '商品',
      backTo: '返回'
    };
  }

  getActiveSidebarLink() {
    const currentPath = window.location.pathname;
    const activeLink = document.querySelector('.collection-navigation .collection-nav-link.active');

    if (activeLink) {
      return activeLink;
    }

    const sidebarLinks = document.querySelectorAll('.collection-navigation .collection-nav-link');

    for (const link of sidebarLinks) {
      try {
        const linkUrl = new URL(link.href, window.location.origin);

        if (linkUrl.pathname === currentPath) {
          return link;
        }
      } catch (error) {
        // Ignore invalid links
      }
    }

    return null;
  }

  getDirectLinkFromElement(element) {
    if (!element) return null;

    for (const child of element.children) {
      if (child.classList && child.classList.contains('collection-nav-link')) {
        return child;
      }
    }

    return null;
  }

  getCollectionBreadcrumbItems() {
    const items = [];
    const usedItems = new Set();
    const activeLink = this.getActiveSidebarLink();

    const addItem = (link, isCurrentCollection = false) => {
      if (!link) return;

      const title = link.textContent.trim();

      if (!title) return;

      const url = link.getAttribute('href') || window.location.pathname;
      const key = `${url}|${title}`;

      if (usedItems.has(key)) return;

      usedItems.add(key);

      items.push({
        title,
        url,
        isCurrentCollection
      });
    };

    if (!activeLink) {
      const fallbackName = this.getCollectionName();

      return [
        {
          title: fallbackName,
          url: window.location.pathname,
          isCurrentCollection: true
        }
      ];
    }

    const mainCategory = activeLink.closest('.collection-nav-item.main-category');
    const level2 = activeLink.closest('.collection-nav-subitem.level-2');
    const level3 = activeLink.closest('.collection-nav-subitem.level-3');

    if (mainCategory) {
      addItem(
        this.getDirectLinkFromElement(mainCategory),
        activeLink === this.getDirectLinkFromElement(mainCategory)
      );
    }

    if (level2) {
      addItem(
        this.getDirectLinkFromElement(level2),
        activeLink === this.getDirectLinkFromElement(level2)
      );
    }

    if (level3) {
      addItem(
        this.getDirectLinkFromElement(level3),
        activeLink === this.getDirectLinkFromElement(level3)
      );
    }

    if (items.length === 0) {
      addItem(activeLink, true);
    }

    if (items.length > 0) {
      items[items.length - 1].isCurrentCollection = true;
    }

    return items;
  }

  buildBreadcrumb() {
    const labels = this.getLocaleLabels();
    const collectionItems = this.getCollectionBreadcrumbItems();

    const currentCollectionItem = collectionItems.length
      ? collectionItems[collectionItems.length - 1]
      : {
          title: this.getCollectionName(),
          url: window.location.pathname
        };

    const productName =
      this.currentProductTitle ||
      this.currentProduct?.title ||
      labels.products;

    const backUrl = currentCollectionItem.url || window.location.pathname;
    const backText = currentCollectionItem.title || labels.collections;

    const collectionBreadcrumbHtml = collectionItems.map((item, index) => {
      const isLastCollection = index === collectionItems.length - 1;
      const linkClass = isLastCollection ? 'breadcrumb-link js-back-to-grid' : 'breadcrumb-link';

      return `
        <li class="breadcrumb-item">
          <span class="breadcrumb-separator">&gt;</span>
          <a href="${item.url}" class="${linkClass}">
            ${item.title}
          </a>
        </li>
      `;
    }).join('');

    return `
      <div class="back-to-collection-wrapper">
        <a href="${backUrl}" class="back-to-collection-link js-back-to-grid">
          <svg class="back-arrow" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
          <span>${labels.backTo} ${backText}</span>
        </a>
      </div>

      <nav class="breadcrumb" aria-label="Breadcrumb navigation">
        <ol class="breadcrumb-list">
          <li class="breadcrumb-item">
            <a href="${this.localizePath('/')}" class="breadcrumb-link">
              ${labels.home}
            </a>
          </li>

          <li class="breadcrumb-item">
            <span class="breadcrumb-separator">&gt;</span>
            <a href="${this.localizePath('/collections')}" class="breadcrumb-link">
              ${labels.collections}
            </a>
          </li>

          ${collectionBreadcrumbHtml}

          <li class="breadcrumb-item breadcrumb-current">
            <span class="breadcrumb-separator">&gt;</span>
            <span class="breadcrumb-text">${productName}</span>
          </li>
        </ol>
      </nav>
    `;
  }

  getCollectionName() {
    const activeSidebarLink = this.getActiveSidebarLink();

    if (activeSidebarLink) {
      const activeText = activeSidebarLink.textContent.trim();

      if (activeText) {
        return activeText;
      }
    }

    const collectionTitle = document.querySelector('.collection-title');

    if (collectionTitle) {
      return collectionTitle.textContent.trim().replace(/\([^)]*\)/, '').trim();
    }

    const pathParts = window.location.pathname.split('/');
    const collectionsIndex = pathParts.indexOf('collections');

    if (collectionsIndex !== -1 && pathParts[collectionsIndex + 1]) {
      const handle = decodeURIComponent(pathParts[collectionsIndex + 1]);

      return handle.split('-').map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }).join(' ');
    }

    return this.getLocaleLabels().products;
  }

  renderExtractedContent(htmlContent, stylesHtml = '', variantId = null, stickyAtcPanelHtml = '') {
    const breadcrumbHtml = this.buildBreadcrumb();

    const contentHtml = `
      ${stylesHtml}
      <div class="product-ajax-container cc-main-product">
        ${breadcrumbHtml}
        <div class="product-content-wrapper">
          ${htmlContent}
        </div>
        ${stickyAtcPanelHtml}
      </div>
    `;

    this.grid.innerHTML = contentHtml;

    window.scrollTo({ top: 0, behavior: 'smooth' });

    this.loadProductStyles();
    this.executeProductScripts();

    setTimeout(() => {
      this.initializeProductForms();
      this.readAddonCheckboxes();

      if (variantId) {
        this.selectVariant(variantId);
      }
    }, 500);
  }

  loadProductStyles() {
    const cssFiles = [
      'product.css',
      'product-page.css',
      'media-gallery.css',
      'product-message.css',
      '3dmark-sticky-atc-panel.css'
    ];

    cssFiles.forEach((filename) => {
      const existingLink = document.querySelector(`link[href*="${filename}"]`);

      if (!existingLink) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = `https://3dmart-website.myshopify.com/cdn/shop/t/3/assets/${filename}`;
        document.head.appendChild(link);
      }
    });
  }

  executeProductScripts() {
    const scripts = this.grid.querySelectorAll('script');

    scripts.forEach((oldScript) => {
      if (oldScript.type === 'application/json' || oldScript.type === 'application/ld+json') {
        return;
      }

      const newScript = document.createElement('script');

      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });

      if (oldScript.src) {
        newScript.src = oldScript.src;
      } else {
        newScript.textContent = oldScript.textContent;
      }

      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  initializeProductForms() {
    if (!customElements.get('product-form')) return;

    const productForms = this.grid.querySelectorAll('product-form');

    productForms.forEach((elem) => {
      if (elem.hasAttribute('data-3dmark-subtotal-initialized')) return;

      const form = elem.querySelector('.js-product-form');
      const section = elem.closest('.js-product');
      const subtotalEl = section?.querySelector('[data-3dmark-subtotal]');

      if (typeof elem.init === 'function') {
        try {
          elem.init();

          if (!elem.hasAttribute('data-3dmark-subtotal-initialized')) {
            this.manuallySetupProductForm(elem, form, section, subtotalEl);
          } else if (typeof elem.updateSubtotal === 'function') {
            elem.updateSubtotal();
          }
        } catch (error) {
          this.manuallySetupProductForm(elem, form, section, subtotalEl);
        }
      } else {
        this.manuallySetupProductForm(elem, form, section, subtotalEl);
      }
    });
  }

  manuallySetupProductForm(elem, form, section, subtotalEl) {
    if (!form || !section) return;

    elem.setAttribute('data-3dmark-subtotal-initialized', 'true');
    elem.form = form;
    elem.section = section;
    elem._priceCache = new Map();
    elem._isAddingToCart = false;

    form.addEventListener('change', () => {
      this.updateSubtotalManually(elem, form, section, subtotalEl);
    });

    section.addEventListener('change', (event) => {
      if (event.target?.matches?.('[data-addon-checkbox]')) {
        this.updateSubtotalManually(elem, form, section, subtotalEl);
      }
    });

    form.addEventListener(
      'submit',
      (event) => {
        this.handleAddToCartWithAddons(event, elem, form, section);
      },
      { capture: true }
    );

    this.updateSubtotalManually(elem, form, section, subtotalEl);
  }

  async handleAddToCartWithAddons(event, elem, form, section) {
    if (window.theme?.settings?.afterAtc === 'no-js') {
      return;
    }

    if (elem._isAddingToCart) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const submitBtn = form.querySelector('[name="add"]');

    elem._isAddingToCart = true;

    submitBtn?.setAttribute('aria-disabled', 'true');
    submitBtn?.classList.add('is-loading');

    try {
      const formData = new FormData(form);
      const quantity = parseInt(formData.get('quantity'), 10) || 1;

      const response = await fetch(window.theme?.routes?.cartAdd || this.localizePath('/cart/add.js'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      await response.json();

      const addonChecks = section.querySelectorAll('[data-addon-checkbox]:checked');

      for (const checkbox of addonChecks) {
        const addonVariantId = checkbox.dataset.variantId;

        if (addonVariantId && /^\d+$/.test(addonVariantId)) {
          const productTitle = section.querySelector('.product-title')?.textContent?.trim() || '';

          const addonResponse = await fetch(window.theme?.routes?.cartAdd || this.localizePath('/cart/add.js'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items: [
                {
                  id: parseInt(addonVariantId, 10),
                  quantity,
                  properties: {
                    '_addon_only': 'true',
                    '_addon_for': productTitle
                  }
                }
              ]
            })
          });

          if (!addonResponse.ok) {
            console.error('Failed to add addon:', addonVariantId);
          }
        }
      }

      const cartResponse = await fetch(this.localizePath('/cart.js'), {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        const cartIconBubble = document.querySelector('.cart-count-bubble span');

        if (cartIconBubble) {
          cartIconBubble.textContent = cartData.item_count;
        }
      }

      const cartDrawer = document.querySelector('cart-drawer');

      if (cartDrawer && window.theme?.settings?.afterAtc === 'drawer') {
        if (typeof cartDrawer.refresh === 'function') {
          try {
            await cartDrawer.refresh();
          } catch (refreshError) {
            console.error('Cart drawer refresh failed:', refreshError);
          }

          setTimeout(() => {
            if (typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            }
          }, 300);
        }
      }

      submitBtn?.classList.add('is-success');
    } catch (error) {
      console.error('Add to cart failed:', error);
    } finally {
      submitBtn?.removeAttribute('aria-disabled');

      setTimeout(() => {
        submitBtn?.classList.remove('is-loading');
        submitBtn?.classList.remove('is-success');
        elem._isAddingToCart = false;
      }, 1400);
    }
  }

  async updateSubtotalManually(elem, form, section, subtotalEl) {
    if (!subtotalEl) return;

    const qtyInput = form.querySelector('[name="quantity"]');
    const qty = parseInt(qtyInput?.value || '1', 10) || 1;
    const variantIdInput = form.querySelector('input[name="id"]');
    const mainVariantId = variantIdInput?.value;

    let mainPrice = 0;

    if (mainVariantId && /^\d+$/.test(mainVariantId)) {
      try {
        const res = await fetch(this.localizePath(`/variants/${mainVariantId}.json`));
        const json = await res.json();
        mainPrice = Math.round(parseFloat(json.product_variant?.price || 0) * 100);
      } catch (error) {
        console.warn('Failed to fetch main variant price:', error);
      }
    }

    let addonsTotal = 0;
    const addonChecks = section.querySelectorAll('[data-addon-checkbox]:checked');

    for (const checkbox of addonChecks) {
      const addonId = checkbox.dataset.variantId;

      if (addonId && /^\d+$/.test(addonId)) {
        try {
          const res = await fetch(this.localizePath(`/variants/${addonId}.json`));
          const json = await res.json();
          const price = Math.round(parseFloat(json.product_variant?.price || 0) * 100);

          addonsTotal += price;
        } catch (error) {
          console.warn('Failed to fetch addon price:', addonId, error);
        }
      }
    }

    const subtotalCents = (mainPrice + addonsTotal) * qty;

    subtotalEl.textContent = this.formatMoney(subtotalCents);
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat);
    }

    const currency = window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || 'en-US';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency
    }).format(cents / 100);
  }

  readAddonCheckboxes() {
    const allCheckboxes = this.grid.querySelectorAll('[data-addon-checkbox]');

    if (allCheckboxes.length > 0) {
      console.log(`Found ${allCheckboxes.length} addon checkboxes`);
    }
  }

  restoreGrid(updateHistory = true) {
    const hasValidContent =
      this.originalContent &&
      !this.originalContent.includes('brand-products-loading') &&
      this.originalContent.includes('product-card');

    if (!hasValidContent) {
      const currentPath = window.location.pathname;
      const currentParams = new URLSearchParams(window.location.search);

      currentParams.delete('view');
      currentParams.delete('variant');

      const newUrl = currentParams.toString()
        ? `${currentPath}?${currentParams.toString()}`
        : currentPath;

      window.location.href = newUrl;
      return;
    }

    this.grid.innerHTML = this.originalContent;

    if (updateHistory) {
      const currentPath = window.location.pathname;
      const currentParams = new URLSearchParams(window.location.search);

      currentParams.delete('view');
      currentParams.delete('variant');

      const newUrl = currentParams.toString()
        ? `${currentPath}?${currentParams.toString()}`
        : currentPath;

      window.history.pushState({ type: 'collection' }, '', newUrl);
    }

    this.reinitializeComponents();
  }

  setupHistoryHandling() {
    window.addEventListener('popstate', () => {
      const urlParams = new URLSearchParams(window.location.search);
      const viewParam = urlParams.get('view');
      const variantParam = urlParams.get('variant');

      if (viewParam) {
        const productUrl = this.buildProductUrlFromHandle(viewParam);
        this.loadProduct(productUrl, false, variantParam);
      } else {
        const brandParam = urlParams.get('brand');

        if (brandParam && this.originalContent) {
          this.restoreGrid(false);
        } else if (brandParam) {
          window.location.reload();
        } else {
          this.restoreGrid(false);
        }
      }
    });
  }

  checkForProductView() {
    const urlParams = new URLSearchParams(window.location.search);
    const viewParam = urlParams.get('view');
    const variantParam = urlParams.get('variant');

    if (viewParam && this.grid) {
      const productUrl = this.buildProductUrlFromHandle(viewParam);
      this.loadProduct(productUrl, false, variantParam);
    }
  }

  reinitializeComponents() {
    document.dispatchEvent(new CustomEvent('grid:restored'));
  }

  selectVariant(variantId) {
    const variantInput = this.grid.querySelector(`input[name="id"][value="${variantId}"]`);
    const variantPicker = this.grid.querySelector('variant-picker');

    if (variantInput) {
      variantInput.checked = true;
      variantInput.click();
    }

    if (variantPicker) {
      const productData = variantPicker.getProductData ? variantPicker.getProductData() : null;

      if (productData && productData.product) {
        const targetVariant = productData.product.variants.find((variant) => {
          return String(variant.id) === String(variantId);
        });

        if (targetVariant) {
          targetVariant.options.forEach((optionValue, index) => {
            const selector = variantPicker.querySelectorAll('.option-selector')[index];

            if (selector) {
              const optionInput = selector.querySelector(`input[value="${optionValue}"]`);

              if (optionInput) {
                optionInput.checked = true;
                optionInput.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          });
        }
      }
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductGridAjax();
  });
} else {
  new ProductGridAjax();
}